'use client';

import React from 'react';
import styled from 'styled-components';
import { StatCard } from '@/components/cards';
import { Delta } from '../Delta';

export interface MetricCardWithChangeProps {
  label: string;
  value: number | string;
  change?: number;
  changeLabel?: string;
  changeReverse?: boolean;
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger';
  isWarning?: boolean;
  loading?: boolean;
  className?: string;
  onClick?: () => void;
}

const CardContainer = styled.div`
  position: relative;
`;

const DeltaOverlay = styled.div`
  position: absolute;
  top: 60px;
  right: 24px;
`;

/**
 * MetricCardWithChange Component
 * 
 * A metric card component that displays a value with a change indicator overlay.
 * Wraps the StatCard component and adds a Delta component showing sign-based trends:
 * - Positive changes: green
 * - Negative changes: red
 * - Zero changes: neutral gray
 * 
 * Note: The `isWarning` prop is deprecated and no longer affects color.
 * All deltas now follow strict sign-based coloring.
 * 
 * @component
 * @example
 * ```tsx
 * <MetricCardWithChange
 *   label="Sites Monitored"
 *   value={10}
 *   change={3}
 *   variant="primary"
 * />
 * 
 * <MetricCardWithChange
 *   label="Active Incidents"
 *   value={5}
 *   change={2}
 *   variant="danger"
 * />
 * ```
 */
export const MetricCardWithChange: React.FC<MetricCardWithChangeProps> = ({
  label,
  value,
  change,
  changeLabel,
  changeReverse = false,
  variant = 'default',
  isWarning = false, // Deprecated: kept for backward compatibility but no longer used
  loading = false,
  className,
  onClick,
}) => {
  return (
    <CardContainer className={className}>
      <StatCard
        label={label}
        value={value}
        variant={variant}
        loading={loading}
        onClick={onClick}
      />
      {!loading && change !== 0&&change && (
        <DeltaOverlay>
          <Delta 
            value={change} 
            changeLabel={changeLabel} 
            changeReverse={changeReverse}
            aria-label={`${label} change`} 
          />
        </DeltaOverlay>
      )}
    </CardContainer>
  );
};

export default MetricCardWithChange;

